import { debounce } from 'lodash';
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ViewState } from 'react-map-gl/dist/esm/exports-maplibre';

const DebouncedViewStateContext = createContext<Partial<ViewState>>({});
const LiveViewStateContext = createContext<Partial<ViewState>>({});

export const useViewState = (live?: boolean) => {
  const context = useContext(live ? LiveViewStateContext : DebouncedViewStateContext);
  if (!context) {
    throw new Error('Unable to find Map ViewState context');
  }
  return context;
};

type ViewStateProviderProps = PropsWithChildren<{ value: Partial<ViewState> }>;

export const ViewStateProvider = ({ value: viewState, children }: ViewStateProviderProps) => {
  const [consumerViewState, setConsumerViewState] = useState(viewState);

  // only update value for consumers after map movements settle down
  const debouncedSetState = useMemo(() => debounce(setConsumerViewState, 250), []);

  useEffect(() => {
    debouncedSetState(viewState);
  }, [debouncedSetState, viewState]);

  return (
    <LiveViewStateContext.Provider value={viewState}>
      <DebouncedViewStateContext.Provider value={consumerViewState}>
        {children}
      </DebouncedViewStateContext.Provider>
    </LiveViewStateContext.Provider>
  );
};
